import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { first, isEmpty } from 'lodash'

import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import {
  fetchAgentsByOrg,
  fetchDispositionsByOrg,
  fetchScorecardsByOrg,
  fetchTagsByOrg,
  fetchPlaybooksByOrg,
} from '@/reducers/scorecards/scorecards.actions'
import { setData, setDateRangeFilter, setFilter } from '@/reducers/scorecards/scorecards.redux'
import {
  getNestedOptions,
  getOrganizationOptions,
  getHierarchyOrganizationOptions,
  getChannelTypeOptions,
  getScorecardOptions,
  getSectionOptions,
  getPlaylistsOptions,
  getPlaybookOptionsForScorecards,
  hasAnyOmnichannelAccess,
} from '@/utils/helpers'
import { DateRangePickerDefinedReusable } from '@/components/datePickers/DateRangePickerDefinedReusable'
import Select from '@/components/forms/Select'
import { FilterForm } from '@/components/filters/FilterForm'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { WinsFilter } from '@/components/filters/WinsFilter'
import { MultiFilter } from '@/components/filters/MultiFilter'
import { PartialScoresFilter } from '@/components/filters/PartialScoresFilter'
import { fetchPlaylists } from '@/reducers/playlists/playlists.actions'
import { CHANNEL_TYPE } from '@/utils/constants'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'

export const ViewScoresFilters = ({
  handleFilterSubmit,
  hide = {},
  hideFilterButton,
  sticky = true,
  dateType = 'full',
  multiScorecard = false,
  exports = false,
  children,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const isQACopilot = location.pathname.startsWith('/qa-copilot')
  const requestedScorecardType = isQACopilot ? 'copilot' : 'automated'

  const { playlists } = useSelector((state) => state.playlists)

  const { data, filters, loading } = useSelector((state) => state.scorecards)
  const {
    organizationid: currentUserOrgId,
    hierarchy_manager,
    edit_qa_copilot,
  } = useSelector((state) => state.currentUser)
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { organizations } = useSelector((state) => state)

  const [selectedOrgId, setSelectedOrgId] = useState(currentUserOrgId)
  const [selectedScorecards, setSelectedScorecards] = useState([])
  const [selectedSections, setSelectedSections] = useState([])
  const [selectedMeasures, setSelectedMeasures] = useState([])
  const [dateInvalid, setDateInvalid] = useState(false)

  const isBaltoAdmin = currentUserOrgId === 1
  const showManagerHierarchyOrgDropdown =
    !isBaltoAdmin && hierarchy_manager && edit_qa_copilot && isQACopilot // needed here because this is shared with non-QA Copilot scorecards
  const hasOrganizationDropdown = isBaltoAdmin || showManagerHierarchyOrgDropdown
  const isCopilotAdvancedExports = location.pathname.startsWith('/qa-copilot/exports')
  const isActivityPage =
    location.pathname.startsWith('/scorecards/activity') ||
    location.pathname.startsWith('/qa-copilot/activity')
  const isScorecardExportPage = location.pathname.startsWith('/scorecards/export')
  // Scored status belongs anywhere the query can return partially scored calls:
  // view scores plus both export pages. Everywhere else it stays cleared so the
  // backend keeps its scored-only default.
  const showScoredStatusFilter = exports

  const orgActiveChannels = useMemo(() => {
    if (isBaltoAdmin) {
      // This conditional fixes a rare race condition where the organizations endpoint hasn't returned by the time this hook runs.
      // This happens when an org-1 user refreshes a page containing the scores filters, preventing the .find() method below from finding org 1
      if (isEmpty(organizations)) return []

      const selectedOrganization = organizations?.find((org) => org.id === filters.organization)
      return selectedOrganization?.active_channels || []
    }

    return activeChannels
  }, [filters?.organization, organizations])

  const isOmniChannelEnabled = hasAnyOmnichannelAccess(orgActiveChannels)
  const isMessage = filters.callType && filters.callType !== CHANNEL_TYPE.CALL

  useEffect(() => {
    // If something is hidden, we need to reset the filter, so it doesn't get added to api calls on other pages.
    Object.keys(hide).forEach((key) => {
      if (hide[key]) {
        if (key === 'isWin') {
          dispatch(setFilter('isWin', null))
        } else if (key === 'multiScorecard' || key === 'dateRange') {
          // Not arrays so don't reset. Hopefully no new hides get added...
        } else {
          // Can't use initial state because filter initial state is incomplete
          dispatch(setFilter(key, []))
        }
      }
    })
    if (!exports) {
      dispatch(setFilter('groupBy', []))
      dispatch(setFilter('metrics', []))
    }
    if (showScoredStatusFilter) {
      dispatch(setFilter('scoredStatus', 'scored'))
    } else {
      dispatch(setFilter('scoredStatus', ''))
    }
    if (!isActivityPage && !exports) {
      dispatch(setFilter('playlists', []))
    }
    if (exports) {
      dispatch(setFilter('playbooks', []))
    } else {
      dispatch(setFilter('scorecardThreshold', ''))
    }
    dispatch(setFilter('callType', CHANNEL_TYPE.CALL))
  }, [])

  useEffect(() => {
    dispatch(setFilter('organization', selectedOrgId))

    if (selectedOrgId) {
      dispatch(fetchAgentsByOrg(selectedOrgId))
      dispatch(fetchScorecardsByOrg(selectedOrgId, requestedScorecardType))
      dispatch(fetchTagsByOrg(selectedOrgId))
      dispatch(fetchDispositionsByOrg(selectedOrgId))
      dispatch(fetchPlaylists(selectedOrgId, false))
      dispatch(fetchPlaybooksByOrg(selectedOrgId))
    }
  }, [selectedOrgId])

  useEffect(() => {
    if (selectedOrgId && !hide.scorecards) {
      const scorecardNotSelected = first(filters.scorecards)?.value && isEmpty(selectedScorecards)
      if (scorecardNotSelected) {
        const scorecard = data.scorecardConfigs?.find(
          (scorecard) => scorecard.id === first(filters.scorecards)?.value
        )
        if (scorecard) {
          setSelectedScorecards([scorecard])
          dispatch(setData('targetScorecardConfig', scorecard))
        }
      }
    } else {
      setSelectedScorecards([])
    }
  }, [data.scorecardConfigs])

  const getSelectedScorecardFilters = () => {
    const scorecardFilters = []
    if (!isEmpty(selectedScorecards)) {
      selectedScorecards.forEach((scorecard) => {
        const payload = {
          id: scorecard.id,
          value: scorecard.id,
          label: scorecard.name,
          sid: scorecard.sid, // Needed for aggregate endpoint
        }
        scorecardFilters.push(payload)
      })
    }
    return scorecardFilters
  }

  const organizationOptions = useMemo(() => {
    return getOrganizationOptions(organizations)
  }, [organizations])

  const hierarchyOrganizationOptions = useMemo(
    () => getHierarchyOrganizationOptions(flattenedUserOrgHierarchy),
    [flattenedUserOrgHierarchy?.length]
  )

  const scorecardOptions = useMemo(() => {
    const options = getScorecardOptions(data.scorecardConfigs)
    if (!selectedOrgId) {
      return []
    }

    if (filters.callType) {
      return options.filter((config) => config.callType === filters.callType)
    }

    return options
  }, [data.scorecardConfigs, selectedOrgId, filters.callType])

  const playbookOptions = useMemo(() => {
    return getPlaybookOptionsForScorecards(data.playbooks)
  }, [data.playbooks, selectedOrgId])

  const playlistsOptions = useMemo(() => {
    return selectedOrgId ? getPlaylistsOptions(playlists) : []
  }, [playlists, selectedOrgId])

  const sectionOptions = useMemo(() => {
    return getSectionOptions(first(selectedScorecards))
  }, [selectedScorecards])

  const measureOptions = useMemo(() => {
    return getNestedOptions(selectedSections, 'measures')
  }, [selectedSections])

  const criteriaOptions = useMemo(() => {
    return getNestedOptions(selectedMeasures, 'criteria')
  }, [selectedMeasures])

  // ** CLEAR DEPENDENT FILTERS **
  const resetMeasuresDependentFilters = () => {
    dispatch(setFilter('criteria', []))
  }

  const resetSectionsDependentFilters = () => {
    dispatch(setFilter('measures', []))
    setSelectedMeasures([])
    resetMeasuresDependentFilters()
  }

  const resetScorecardDependentFilters = () => {
    dispatch(setFilter('sections', []))
    setSelectedSections([])
    resetSectionsDependentFilters()
  }

  const resetGroupByAndMetricsFilters = () => {
    dispatch(setFilter('groupBy', []))
    dispatch(setFilter('metrics', []))
    resetSectionsDependentFilters()
  }

  const resetOrgDependentFilters = () => {
    dispatch(setFilter('scorecards', []))
    setSelectedScorecards([])
    dispatch(setFilter('playlists', []))
    dispatch(setFilter('playbooks', []))
    dispatch(setFilter('agents', []))
    dispatch(setFilter('tags', []))
    dispatch(setFilter('dispositions', []))
    resetScorecardDependentFilters()
  }

  const filterIsDisabled = () => {
    // Disable button if date is over 62 days
    if (loading.scores || dateInvalid) return true

    // Disable button if scorecard is not selected
    // Multi-scorecard treats empty selection as "All Scorecards"
    if (multiScorecard) return false

    return isEmpty(filters.scorecards)
  }

  // FORM SELECTOR COMMON METHODS
  const selectScorecard = (scorecardId) => {
    if (scorecardId) {
      const selectedScorecard = data.scorecardConfigs?.find(
        (scorecard) => scorecard.id === scorecardId
      )
      const payload = {
        id: selectedScorecard?.id,
        value: selectedScorecard?.id,
        label: selectedScorecard?.name,
        sid: selectedScorecard?.sid,
      }
      resetScorecardDependentFilters()
      setSelectedScorecards([selectedScorecard])
      dispatch(setData('targetScorecardConfig', selectedScorecard))
      dispatch(setFilter('scorecards', [payload]))
    }
  }

  const selectSections = (sectionIds) => {
    const filteredSections = []
    const filteredSectionsOption = []
    if (sectionIds?.length > 0 && selectedScorecards) {
      sectionIds.forEach((sectionId) => {
        const sectionFound = first(selectedScorecards)?.sections?.find((section) => {
          return section.id === sectionId
        })
        if (sectionFound) {
          filteredSections.push(sectionFound)
          filteredSectionsOption.push({
            value: sectionFound.id,
            label: sectionFound.name,
            ssid: sectionFound.ssid,
          })
        }
      })
    }
    resetSectionsDependentFilters()
    setSelectedSections(filteredSections)
    dispatch(setFilter('sections', filteredSectionsOption))
  }

  const selectMeasures = (measureIds) => {
    const filteredMeasures = []
    const filteredMeasuresOption = []
    if (measureIds?.length > 0 && selectedSections?.length > 0) {
      measureIds.forEach((id) => {
        selectedSections.forEach((section) => {
          const measureFound = section?.measures?.find((measure) => measure.id === id)
          if (measureFound) {
            filteredMeasures.push(measureFound)
            filteredMeasuresOption.push({
              value: measureFound.id,
              label: measureFound.name,
              smid: measureFound.smid,
            })
          }
        })
      })
    }

    resetMeasuresDependentFilters()
    setSelectedMeasures(filteredMeasures)
    dispatch(setFilter('measures', filteredMeasuresOption))
  }

  useEffect(() => {
    // QA Copilot has only one measure per section, so we select those by default
    if (!isEmpty(selectedSections) && isQACopilot) {
      const newMeasureOptions = []
      selectedSections.forEach((section) => {
        const firstMeasure = section.measures[0]
        if (firstMeasure) {
          newMeasureOptions.push(firstMeasure.id)
        }
      })
      selectMeasures(newMeasureOptions)
    }
  }, [measureOptions])

  // ** FORM HANDLERS **
  const handleOrgSelect = (selectedOrgLabel) => {
    setSelectedOrgId(selectedOrgLabel?.value ?? null)
    resetOrgDependentFilters()
  }

  const handleMultiScorecardConfigSelect = (multiScorecardOptions) => {
    const selectedScorecardIds = multiScorecardOptions.map(({ value }) => value)
    const selectedScorecards = data.scorecardConfigs?.filter((scorecard) =>
      selectedScorecardIds.includes(scorecard.id)
    )
    resetScorecardDependentFilters()
    resetGroupByAndMetricsFilters()
    setSelectedScorecards(selectedScorecards)
    dispatch(setFilter('scorecards', multiScorecardOptions))
  }

  const handleScorecardConfigSelect = (selectedScorecardsLabel) => {
    const selectedScorecardsId = selectedScorecardsLabel?.value ?? null
    selectScorecard(selectedScorecardsId)
  }

  const handleSectionsSelect = (sectionOptions) => {
    const sectionIds = sectionOptions.map((option) => option.value)
    selectSections(sectionIds)
  }

  const handleUpdateFilters = (section, value) => {
    dispatch(setFilter(section, value))
  }

  const handleUpdateChannel = (option) => {
    handleUpdateFilters('callType', option.value)
    resetScorecardDependentFilters()
    setSelectedScorecards([])
    handleUpdateFilters('scorecards', [])
  }

  const handleScorecardsThreshold = (threshold) => {
    handleUpdateFilters('scorecardThreshold', threshold)
  }

  const handleMeasuresSelect = (measuresOptions) => {
    const measureIds = measuresOptions.map((option) => option.value)
    selectMeasures(measureIds)
  }

  const selectCriteria = (criteriaIds) => {
    const filteredCriteriaOptions = []
    if (criteriaIds?.length > 0 && selectedSections?.length > 0) {
      criteriaIds.forEach((id) => {
        selectedMeasures.forEach((measure) => {
          const criteriaFound = measure?.criteria?.find((criteria) => criteria.id === id)
          if (criteriaFound) {
            filteredCriteriaOptions.push({
              value: criteriaFound.id,
              label: criteriaFound.name,
              scid: criteriaFound.scid,
            })
          }
        })
      })
    }
    dispatch(setFilter('criteria', filteredCriteriaOptions))
  }

  const handleCriteriaSelect = (criteriaOptions) => {
    const criteriaIds = criteriaOptions.map((option) => option.value)
    selectCriteria(criteriaIds)
  }

  const handleDateRangeFilterChange = (value) => {
    const formattedDate = {}
    formattedDate.startDate = moment(value.startDate).startOf('day').utc().format()
    formattedDate.endDate = moment(value.endDate).endOf('day').utc().format()
    const datesDiff = moment.utc(value.endDate).diff(moment.utc(value.startDate), 'days')
    if (datesDiff > 62) {
      setDateInvalid(true)
    } else {
      setDateInvalid(false)
      dispatch(setDateRangeFilter(formattedDate))
    }
  }

  const emptyCriteria = criteriaOptions?.length === 0
  const emptyMeasures = measureOptions?.length === 0
  const emptySections = sectionOptions?.length === 0
  const criteriaPlaceHolderText = emptyCriteria ? 'No criteria found' : 'Select Criteria'
  const measurePlaceHolderText = emptyMeasures ? 'No measures found' : 'Select Measures'
  const sectionPlaceHolderText = emptySections ? 'No sections found' : 'Select Sections'

  return (
    <div className="view-scores-filters" data-testid="view-scores-filters">
      <FilterForm
        medium
        sticky={sticky}
        data-testid="view-scores-form"
        onSubmit={() => handleFilterSubmit?.()}
        actions={
          !hideFilterButton && (
            <Button
              brand
              type="submit"
              data-testid="filter-button"
              loading={loading.scores}
              disabled={filterIsDisabled()}
              disabledTooltip={
                isEmpty(filters.scorecards)
                  ? 'Please select a scorecard'
                  : dateInvalid
                    ? 'Please select a valid date range'
                    : ''
              }
            >
              Filter
            </Button>
          )
        }
      >
        {!hide.organization && hasOrganizationDropdown && (
          <OrganizationFilter
            fixedWidth={false}
            options={
              showManagerHierarchyOrgDropdown ? hierarchyOrganizationOptions : organizationOptions
            }
            value={selectedOrgId}
            onChange={handleOrgSelect}
          />
        )}
        {isOmniChannelEnabled && isCopilotAdvancedExports && (
          <FormField data-testid="channel-picker">
            <label>Channel</label>
            <Select
              onChange={handleUpdateChannel}
              isClearable={false}
              value={filters.callType}
              options={getChannelTypeOptions(orgActiveChannels)}
            />
          </FormField>
        )}
        {!hide.dateRange && dateType === 'full' && (
          <FormField data-testid="date-range-field">
            <label>Date Range</label>
            <DateRangePickerReusable
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={({ selection }) => {
                handleDateRangeFilterChange(selection)
              }}
              error={dateInvalid}
            />

            {dateInvalid && (
              <ErrorMessage
                content="Date range must be two months or less"
                data-testid="datepicker-error"
              />
            )}
          </FormField>
        )}
        {!hide.dateRange && dateType === 'defined' && (
          <FormField data-testid="date-range-field">
            <label>Date Range</label>
            <DateRangePickerDefinedReusable
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={({ selection }) => {
                handleDateRangeFilterChange(selection)
              }}
              error={dateInvalid}
            />

            {dateInvalid && (
              <ErrorMessage
                content="Date range must be two months or less"
                data-testid="datepicker-error"
              />
            )}
          </FormField>
        )}
        {!hide.agents && (
          <MultiFilter
            fixedWidth={false}
            isSelectAll
            label="Agents"
            accessor="agents"
            store={{ data, filters, loading }}
            onChange={(option) => handleUpdateFilters('agents', option)}
          />
        )}
        {!hide.tags && (
          <MultiFilter
            fixedWidth={false}
            isSelectAll
            label="Tags"
            accessor="tags"
            store={{ data, filters, loading }}
            onChange={(option) => handleUpdateFilters('tags', option)}
          />
        )}

        {!hide.isWin && !isMessage && (
          <WinsFilter
            fixedWidth={false}
            filters={filters}
            onChange={(option, action) => {
              dispatch(setFilter('isWin', action.action === 'clear' ? null : option.value))
            }}
          />
        )}
        {!hide.scorecards && (
          <>
            {!multiScorecard ? (
              <FormField data-testid="scorecard-config-field">
                <label id="scorecards">Scorecards</label>
                <Select
                  placeholder={
                    isEmpty(scorecardOptions) ? 'No scorecards found' : 'Select scorecard'
                  }
                  options={scorecardOptions}
                  aria-labelledby="scorecards"
                  loading={loading.scorecards}
                  isClearable={false}
                  disabled={isEmpty(scorecardOptions)}
                  value={first(selectedScorecards)?.id}
                  onChange={(value) => handleScorecardConfigSelect(value)}
                />
              </FormField>
            ) : (
              <FormField data-testid="scorecard-config-field">
                <label id="scorecards">Scorecards</label>
                <MultiSelect
                  isSelectAll
                  placeholderPill={!isEmpty(scorecardOptions) ? 'All Scorecards' : undefined}
                  placeholder={isEmpty(scorecardOptions) ? 'No scorecards found' : undefined}
                  disabled={isEmpty(scorecardOptions)}
                  options={scorecardOptions}
                  aria-labelledby="scorecards"
                  loading={loading.scorecards}
                  value={getSelectedScorecardFilters()}
                  onChange={(value) => handleMultiScorecardConfigSelect(value)}
                />
              </FormField>
            )}
            {isCopilotAdvancedExports && !isMessage && (
              <FormField data-testid="playbook-filter">
                <label>Playbooks</label>
                <MultiSelect
                  isSelectAll
                  placeholderPill="All Playbooks"
                  options={playbookOptions}
                  loading={loading.playbooks}
                  value={filters.playbooks}
                  onChange={(value) => handleUpdateFilters('playbooks', value)}
                />
              </FormField>
            )}
            {showScoredStatusFilter && (
              <PartialScoresFilter
                fixedWidth={false}
                filters={filters}
                isClearable={false}
                onChange={(option) => {
                  handleUpdateFilters('scoredStatus', option != null ? option.value : '')
                }}
              />
            )}
          </>
        )}
        {(isActivityPage || exports) && (
          <FormField data-testid="playlist-filter">
            <label id="playlists">Playlists</label>
            <MultiSelect
              isSelectAll
              placeholderPill="All Playlists"
              options={playlistsOptions}
              aria-labelledby="playlists"
              value={filters.playlists}
              onChange={(value) => handleUpdateFilters('playlists', value)}
            />
          </FormField>
        )}
        {!hide.dispositions && !isMessage && !isEmpty(data.dispositions) && (
          <MultiFilter
            fixedWidth={false}
            label="Dispositions"
            accessor="dispositions"
            store={{ data, filters, loading }}
            onChange={(option) => handleUpdateFilters('dispositions', option)}
          />
        )}
        {!hide.sections && (
          <FormField
            disabled={emptySections || selectedScorecards.length > 1}
            className={classNames({ 'disabled-field-with-tooltip': emptySections })}
            data-testid={
              emptySections || selectedScorecards.length > 1
                ? 'sections-field-score-filter-disable-form-field'
                : 'sections-field'
            }
          >
            <label id="sections">Sections</label>
            <div data-testid="section-picker">
              <InteractiveTooltip
                block
                content="One scorecard must be selected in order to view the section"
                hide={!emptySections}
              >
                <div>
                  <MultiSelect
                    isSelectAll
                    placeholderPill={!emptySections ? 'All Sections' : undefined}
                    placeholder={emptySections ? sectionPlaceHolderText : undefined}
                    options={sectionOptions}
                    allowSelectAll
                    aria-labelledby="sections"
                    loading={loading.scorecards}
                    value={filters?.sections}
                    onChange={(value) => handleSectionsSelect(value)}
                    disabled={emptySections || selectedScorecards.length > 1}
                  />
                </div>
              </InteractiveTooltip>
            </div>
          </FormField>
        )}
        {!hide.measures && (
          <FormField
            disabled={emptyMeasures}
            className={classNames({ 'disabled-field-with-tooltip': emptyMeasures })}
            data-testid={
              emptyMeasures ? 'measures-field-score-filter-disable-form-field' : 'measures-field'
            }
          >
            <label id="measures">Measures</label>
            <div data-testid="measure-picker">
              <InteractiveTooltip
                block
                content={`One section must be selected in order to view the ${
                  !isQACopilot ? 'measure' : 'criteria'
                }`}
                hide={!emptyMeasures}
              >
                <div>
                  <MultiSelect
                    isSelectAll
                    placeholder={measurePlaceHolderText}
                    options={measureOptions}
                    allowSelectAll
                    aria-labelledby="measures"
                    loading={loading.scorecards}
                    value={filters?.measures}
                    onChange={(value) => handleMeasuresSelect(value)}
                    disabled={emptyMeasures}
                  />
                </div>
              </InteractiveTooltip>
            </div>
          </FormField>
        )}
        {!hide.criteria && (
          <FormField
            disabled={emptyCriteria}
            className={classNames({ 'disabled-field-with-tooltip': emptyCriteria })}
            data-testid={
              emptyCriteria ? 'criteria-field-score-filter-disable-form-field' : 'criteria-field'
            }
          >
            <label id="criteria">Criteria</label>
            <InteractiveTooltip
              block
              content={`One ${
                !isQACopilot ? 'measure' : 'section'
              } must be selected in order to view criteria`}
              hide={!emptyCriteria}
            >
              <div>
                <MultiSelect
                  isSelectAll
                  placeholder={criteriaPlaceHolderText}
                  options={criteriaOptions}
                  disabled={emptyCriteria}
                  aria-labelledby="criteria"
                  loading={loading.scorecards}
                  value={filters?.criteria}
                  onChange={(value) => handleCriteriaSelect(value)}
                />
              </div>
            </InteractiveTooltip>
          </FormField>
        )}
        {isScorecardExportPage && (
          <FormField data-testid="scorecard-threshold-field">
            <label>Threshold</label>
            <div>
              <Select
                placeholder="Select Threshold"
                options={[
                  { label: 'Needs improvement', value: 'improvement_threshold' },
                  { label: 'Meets expectations', value: 'meets_threshold' },
                  { label: 'Exceeds expectations', value: 'exceeds_threshold' },
                ]}
                aria-labelledby="scorecard_threshold"
                value={filters?.scorecardThreshold}
                onChange={(selectedOption) => {
                  if (selectedOption === null) {
                    handleScorecardsThreshold('')
                  } else {
                    handleScorecardsThreshold(selectedOption.value)
                  }
                }}
              />
            </div>
          </FormField>
        )}
        {children}
      </FilterForm>
    </div>
  )
}
